// A retail park option out of the browser: DXF for the architect, IFC for
// the BIM, and the tenancy schedule and options as spreadsheets.
import { Writer } from "../export/dxf.ts";
import { spacesIfc } from "../export/ifc.ts";
import type { Sheet } from "../export/xlsx.ts";
import type { Envelope } from "../envelope.ts";
import type { Pt } from "../geom.ts";
import { centroidOf } from "./masterplan.ts";
import type { RetailGenerated } from "./generate.ts";
import type { RetailFinanceInputs, RetailProForma } from "./finance.ts";
import { KIND_NAME } from "./programme.ts";

export const TYPOLOGY_NAME = { line: "Linie", "L-left": "L", "L-right": "L", U: "U" } as const;
/** The option as a developer names it; a row whose lorry lane runs down one side says which. */
export const optionName = (g: RetailGenerated) => {
  const lane = g.typology === "line" && g.scheme.typology !== "line" ? (g.scheme.typology === "L-left" ? " · livrări pe stânga" : g.scheme.typology === "L-right" ? " · livrări pe dreapta" : " · livrări pe laterale") : "";
  return `${TYPOLOGY_NAME[g.typology]} · adâncime ${g.scheme.depth} m${lane}`;
};

/** Every layer the masterplan draws, in Stereo 70 when the plot was imported. */
export function retailDxf(g: RetailGenerated, env: Envelope, origin: Pt = [0, 0]): string {
  const w = new Writer(origin);
  const s = g.scheme;
  w.poly("TEREN", env.plot);
  w.poly("ANVELOPA", env.storeys[0]?.footprint ?? []);
  w.poly("SITE_VERDE", s.green);
  w.poly("SITE_LIVRARE", s.service);
  w.poly("SITE_LIVRARE", s.lanes);
  w.poly("SITE_DRUMURI", s.frontRoad);
  w.poly("SITE_ACCES", s.access);
  w.poly("SITE_COPERTINA", s.canopy);
  w.poly("PARCARE_ALEI", s.parking.aisles);
  for (const st of s.parking.stalls) w.poly("PARCARE_LOCURI", st);
  for (const st of s.parking.islands) w.poly("PARCARE_INSULE", st);
  for (const u of s.units) {
    w.poly("NIV_P_UNITATI", u.polygon);
    w.line("NIV_P_VITRINE", u.front[0], u.front[1]);
    for (const o of u.service) w.line(o.kind === "dock" ? "NIV_P_CHEIURI" : "NIV_P_USI", o.a, o.b);
    w.text("NIV_P_TEXT", centroidOf(u.polygon), `${u.id} ${KIND_NAME[u.kind]} ${u.gla.toFixed(0)} m2 GLA`, 1.2);
  }
  for (const c of s.columns) w.poly("NIV_P_STALPI", [[[[c[0] - 0.2, c[1] - 0.2], [c[0] + 0.2, c[1] - 0.2], [c[0] + 0.2, c[1] + 0.2], [c[0] - 0.2, c[1] + 0.2]]]]);
  for (const p of s.pads) {
    w.poly("PAD", p.building);
    w.poly("PAD_DRUMURI", p.lane);
    for (const st of p.stalls) w.poly("PARCARE_LOCURI", st);
    w.text("NIV_P_TEXT", centroidOf(p.building), `${p.id} ${p.gla.toFixed(0)} m2`, 1.2);
  }
  return w.toString();
}

/** Spaces as IFC4: one storey, every unit and pad an IfcSpace with its GLA and type. */
export function retailIfc(g: RetailGenerated, name: string, origin: Pt = [0, 0]): string {
  const s = g.scheme;
  return spacesIfc(name, `Retail park ${optionName(g)}`, [{
    name: "Parter", base: 0,
    spaces: [
      ...s.units.map((u) => ({
        name: u.id, kind: KIND_NAME[u.kind], label: `${u.id} ${KIND_NAME[u.kind]}`, polygon: u.polygon, height: g.height - 0.5,
        props: [["Tip", KIND_NAME[u.kind]], ["SuprafataConstruita", +u.area.toFixed(2)], ["GLA", +u.gla.toFixed(2)], ["Front", +u.frontage.toFixed(2)], ["Adancime", +u.depth.toFixed(2)], ["Cheiuri", u.service.filter((o) => o.kind === "dock").length]] as [string, string | number][],
      })),
      ...s.pads.map((p) => ({
        name: p.id, kind: KIND_NAME.pad, label: `${p.id} ${KIND_NAME.pad}`, polygon: p.building, height: 5.5,
        props: [["Tip", KIND_NAME.pad], ["SuprafataConstruita", +p.area.toFixed(2)], ["GLA", +p.gla.toFixed(2)]] as [string, string | number][],
      })),
    ],
  }], origin);
}

const num = (n: number, dp = 2) => n.toFixed(dp).replace(".", ",");
const cell = (v: string | number) => (typeof v === "number" ? num(v) : /[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
const table = (rows: (string | number)[][]) => "﻿" + rows.map((r) => r.map(cell).join(";")).join("\r\n") + "\r\n";

/** The tenancy schedule: one row per unit and pad, with its rent. */
export function unitsRows(g: RetailGenerated, f: RetailFinanceInputs): (string | number)[][] {
  return [
    ...g.scheme.units.map((u) => [u.id, KIND_NAME[u.kind], u.area, u.gla, u.frontage, u.depth, u.service.filter((o) => o.kind === "dock").length, f.rent[u.kind], u.gla * f.rent[u.kind] * 12]),
    ...g.scheme.pads.map((p) => [p.id, KIND_NAME.pad, p.area, p.gla, 0, 0, 0, f.rent.pad, p.gla * f.rent.pad * 12]),
  ];
}
export const UNITS_HEADER = ["Unitate", "Tip", "Construită m²", "GLA m²", "Front m", "Adâncime m", "Cheiuri", "Chirie €/m²/lună", "Chirie anuală €"];

export function unitsCsv(g: RetailGenerated, f: RetailFinanceInputs): string {
  return table([UNITS_HEADER, ...unitsRows(g, f)]);
}

export function retailOptionsRows(opts: { g: RetailGenerated; pf: RetailProForma }[]): (string | number)[][] {
  return opts.map(({ g, pf }) => [optionName(g), g.scheme.units.length, g.scheme.pads.length, g.gla, g.indicators.pot, g.indicators.cut, g.parking.capacity, g.parking.required,
    (g.balance.green / g.balance.plot) * 100, pf.rentRoll, pf.noi, pf.netValue, pf.cost.total, pf.profit, pf.yieldOnCost]);
}
export const OPTIONS_HEADER = ["Variantă", "Unități", "Pad-uri", "GLA m²", "POT %", "CUT", "Locuri", "Necesar", "Verde %", "Chirii €/an", "NOI €/an", "Valoare netă €", "Cost €", "Profit €", "Yield on cost %"];

export function retailOptionsCsv(opts: { g: RetailGenerated; pf: RetailProForma }[], plotArea: number): string {
  return table([OPTIONS_HEADER, ...retailOptionsRows(opts), [], ["Teren m²", plotArea]]);
}

/** The same tables as workbook sheets. */
export function unitsSheet(g: RetailGenerated, f: RetailFinanceInputs, preamble: string[]): Sheet {
  const rows = unitsRows(g, f);
  return {
    name: "Chiriasi", preamble,
    columns: [{ header: "Unitate", width: 9 }, { header: "Tip", width: 16 }, { header: "Construită m²", format: "money" }, { header: "GLA m²", format: "money" }, { header: "Front m", format: "money" }, { header: "Adâncime m", format: "money" }, { header: "Cheiuri" }, { header: "Chirie €/m²/lună", format: "money" }, { header: "Chirie anuală €", format: "money", width: 16 }],
    rows,
    footer: ["Total", `${rows.length}`, rows.reduce((t, r) => t + (r[2] as number), 0), rows.reduce((t, r) => t + (r[3] as number), 0), null, null, rows.reduce((t, r) => t + (r[6] as number), 0), null, rows.reduce((t, r) => t + (r[8] as number), 0)],
  };
}
export function retailOptionsSheet(opts: { g: RetailGenerated; pf: RetailProForma }[], preamble: string[]): Sheet {
  return {
    name: "Variante", preamble,
    columns: OPTIONS_HEADER.map((header, i) => ({ header, width: i === 0 ? 26 : i >= 9 ? 16 : undefined, format: i === 0 ? undefined : i === 4 || i === 5 || i === 8 || i >= 9 ? "money" : undefined })),
    rows: retailOptionsRows(opts),
  };
}
